import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@jem-open/jem-ui';
import axios from 'axios';
import moment from 'moment';
import { environment } from '../../../../environment/environment';
import apiResponseHandler from '../../../../utils/apiResponseHandler';
import toast from '../../../../utils/toast';

interface SyncPauseBannerProps {
  employeeId: number;
  employerId: number;
  syncPausedBy: number | null | undefined;
  syncPausedByName: string | null | undefined;
  syncPauseReason: string | null | undefined;
  syncPausedUntil: string | null | undefined;
  onUpdate: () => void;
}

function isSyncPaused(syncPausedUntil: string | null | undefined): boolean {
  if (!syncPausedUntil) return false;
  return moment.utc(syncPausedUntil).isAfter(moment.utc());
}

const INTEGRATIONS_FEATURE_NAME = 'Integrations';

const PauseDetails: React.FC<{ pausedByName: string | null; syncPauseReason: string | null | undefined }> = ({
  pausedByName,
  syncPauseReason,
}) => (
  <>
    {pausedByName && (
      <div className="mt-xxs">
        <strong>Paused by:</strong> {pausedByName}
      </div>
    )}
    {syncPauseReason && (
      <div className="mt-xxs">
        <strong>Reason:</strong> {syncPauseReason}
      </div>
    )}
  </>
);

const SyncPauseBanner: React.FC<SyncPauseBannerProps> = ({
  employeeId,
  employerId,
  syncPausedBy,
  syncPausedByName: syncPausedByNameProp,
  syncPauseReason,
  syncPausedUntil,
  onUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [hasIntegrations, setHasIntegrations] = useState(false);
  const [pausedByName, setPausedByName] = useState<string | null>(null);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState('');
  const [validationError, setValidationError] = useState('');
  const paused = isSyncPaused(syncPausedUntil);
  const expired = !paused && !!syncPausedUntil;

  useEffect(() => {
    let current = true;
    setHasIntegrations(false);
    axios
      // eslint-disable-next-line camelcase
      .get(`${environment.baseUrl}/employer_features/`, { params: { employer_id: employerId } })
      .then((res) => {
        if (!current) return;
        const integrations = res.data.find(
          (f: { name: string; enabled: boolean }) => f.name === INTEGRATIONS_FEATURE_NAME && f.enabled
        );
        setHasIntegrations(!!integrations);
      })
      .catch(() => {
        if (current) setHasIntegrations(false);
      });
    return () => { current = false; };
  }, [employerId]);

  useEffect(() => {
    if (syncPausedByNameProp) {
      setPausedByName(syncPausedByNameProp);
      return undefined;
    }
    if (!syncPausedBy) {
      setPausedByName(null);
      return undefined;
    }
    let current = true;
    axios
      .get(`${environment.baseUrl}/users/${syncPausedBy}/`)
      .then((res) => {
        if (!current) return;
        const { first_name: firstName, last_name: lastName, email } = res.data;
        setPausedByName(firstName && lastName ? `${firstName} ${lastName}` : email || `User ${syncPausedBy}`);
      })
      .catch(() => {
        if (current) setPausedByName(`User ${syncPausedBy}`);
      });
    return () => { current = false; };
  }, [syncPausedBy, syncPausedByNameProp]);

  if (!hasIntegrations && !paused && !expired) return null;

  const openPauseDialog = () => {
    setReason('');
    setUntil('');
    setValidationError('');
    setPauseDialogOpen(true);
  };

  const handlePauseConfirm = async () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason || !until) {
      setValidationError('Both reason and date are required.');
      return;
    }
    if (moment.utc(until, 'YYYY-MM-DD').isBefore(moment.utc(), 'day')) {
      setValidationError('Date must be today or later.');
      return;
    }

    setLoading(true);
    setPauseDialogOpen(false);
    try {
      await axios.post(`${environment.baseUrl}/employees/${employeeId}/sync-pause/`, {
        sync_pause_reason: trimmedReason, // eslint-disable-line camelcase
        sync_paused_until: moment.utc(until, 'YYYY-MM-DD').endOf('day').toISOString(), // eslint-disable-line camelcase
      });
      toast.success('Sync paused successfully');
      onUpdate();
    } catch (error) {
      apiResponseHandler.handleApiError('Error', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnpauseConfirm = async () => {
    setLoading(true);
    setResumeDialogOpen(false);
    try {
      await axios.post(`${environment.baseUrl}/employees/${employeeId}/sync-unpause/`);
      toast.success('Sync resumed successfully');
      onUpdate();
    } catch (error) {
      apiResponseHandler.handleApiError('Error', error);
    } finally {
      setLoading(false);
    }
  };

  const pauseButton = (
    <Button variant="primary" size="sm" onClick={openPauseDialog} disabled={loading} loading={loading}>
      Pause Sync
    </Button>
  );

  const pauseDialog = (
    <Dialog open={pauseDialogOpen} onOpenChange={(next) => { if (!next) setPauseDialogOpen(false); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pause Data Sync</DialogTitle>
          <DialogDescription className="text-sm">
            The data sync integration will not update this employee until the date specified.
            Manual edits made during this period will persist.
            This is a dangerous action: key updates such as terminations or data affecting
            advance eligibility could be missed while the sync is paused.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-sm">
          <div className="flex flex-col gap-xxs">
            <Label htmlFor="pause-reason">
              Reason <span className="text-error-text-label">*</span>
            </Label>
            <p className="m-none text-xs text-greyscale-text-caption italic">
              Viewable by the employer
            </p>
            <Textarea
              id="pause-reason"
              rows={3}
              value={reason}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setReason(e.target.value);
                setValidationError('');
              }}
              placeholder="Why is this pause needed?"
            />
          </div>
          <div className="flex flex-col gap-xxs">
            <Label htmlFor="pause-until">
              Pause until <span className="text-error-text-label">*</span>
            </Label>
            <Input
              id="pause-until"
              type="date"
              value={until}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setUntil(e.target.value);
                setValidationError('');
              }}
            />
          </div>
          {validationError && (
            <p className="m-none text-sm text-error-text-label">{validationError}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPauseDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handlePauseConfirm} disabled={!reason.trim() || !until || loading} loading={loading}>
            Pause Sync
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const resumeDialog = (
    <Dialog open={resumeDialogOpen} onOpenChange={(next) => { if (!next) setResumeDialogOpen(false); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resume Data Sync</DialogTitle>
          <DialogDescription>
            The data sync integration will resume updating this employee on the next run. Are you sure?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setResumeDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUnpauseConfirm} disabled={loading} loading={loading}>
            Resume Sync
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (paused) {
    return (
      <>
        <div className="d-flex justify-content-between align-items-start mb-3 p-3 rounded" role="alert" style={{ backgroundColor: '#ff9a9d' }}>
          <div>
            <strong>Sync Paused:</strong> This employee will not be updated by the data sync
            until <strong>{moment.utc(syncPausedUntil).format('D MMM YYYY')}</strong>.
            <PauseDetails pausedByName={pausedByName} syncPauseReason={syncPauseReason} />
          </div>
          <div className="ml-3" style={{ whiteSpace: 'nowrap' }}>
            <Button variant="primary" size="sm" onClick={() => setResumeDialogOpen(true)} disabled={loading} loading={loading}>
              Resume Sync
            </Button>
          </div>
        </div>
        {resumeDialog}
      </>
    );
  }

  if (expired) {
    return (
      <>
        <div className="d-flex justify-content-between align-items-start mb-3 p-3 rounded" role="alert" style={{ backgroundColor: '#e9ecef' }}>
          <div>
            <em>
              Sync pause expired {moment.utc(syncPausedUntil).format('D MMM YYYY')}.
              <PauseDetails pausedByName={pausedByName} syncPauseReason={syncPauseReason} />
            </em>
          </div>
          {pauseButton}
        </div>
        {pauseDialog}
      </>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 p-3 rounded" role="alert" style={{ backgroundColor: '#ffd0a6' }}>
        <div>
          <strong>Automated Sync Active:</strong> This employee is updated via an automated data sync.
          Any manual changes may be overwritten on the next sync run.
          To make changes that persist, you need to pause the sync first.
        </div>
        <div className="ml-3" style={{ whiteSpace: 'nowrap' }}>
          {pauseButton}
        </div>
      </div>
      {pauseDialog}
    </>
  );
};

export default SyncPauseBanner;
